// React
import { useState } from "react";

// Material UI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Third party
import { v4 as uuidv4 } from "uuid";

// Components
import PositionMappingSectionRow from "@/Settings/components/PositionMapping/PositionMappingSectionRow";

// Types
import { IBasicMappedPosition } from "@Common/types/positions/api";
import { IPositionsMapping, LabelObject } from "@Settings/types/PositionMapping/model";

type PositionMappingSectionProps = {
	id: keyof IPositionsMapping;
	title: string;
	hasErrors: boolean;
	mappedPositions: IBasicMappedPosition[];
	showErrors?: boolean;
	isDropdownRequired: boolean;
	allowDeleteFirst?: boolean;
	hasEditPermission: boolean;
	isLoading: boolean;
	isMutating: boolean;
	onSetAllPositionsMapping: ({
		sectionId,
		updatedMappedPositions,
		updatedAvailablePositions,
	}: {
		sectionId: keyof IPositionsMapping;
		updatedMappedPositions: IBasicMappedPosition[];
		updatedAvailablePositions?: LabelObject[];
	}) => void;
	selectablePositions: LabelObject[];
};

// Constants
const switchablePositions = ["Finance Director", "Parts Counter Associate"];

/********************************************** HELPERS **********************************************/
const getEmptyCells = (items: string[] = []): boolean[] => {
	return items.map((item) => !item);
};

/**
 * Position Mapping Section - This component is responsible for rendering each of the Position Mapping Section.
 */
export default function PositionMappingSection(props: Readonly<PositionMappingSectionProps>) {
	// States
	const [notCollapsed, setNotCollapsed] = useState<string[]>([]);

	// Helpers
	const toggleRowExpansion = (position: string) => {
		setNotCollapsed((prevNotCollapsed) =>
			prevNotCollapsed.includes(position) ? prevNotCollapsed.filter((item) => item !== position) : prevNotCollapsed.concat(position)
		);
	};

	const iconStyle = (position: string) => {
		return {
			transform: notCollapsed.includes(position) ? "rotate(270deg)" : "rotate(90deg)",
			fontSize: "15px",
			position: "relative",
			top: "1px",
			marginLeft: "10px",
		};
	};

	/********************************************** HANDLERS **********************************************/
	const handleUpdateCapSwitch = (systemPosition: string) => {
		const updatedMappedPositions = props.mappedPositions.map((item) =>
			item.systemPosition === systemPosition ? { ...item, isDepartmental: !item.isDepartmental } : item
		);

		const sectionId = props.id;
		props.onSetAllPositionsMapping({ sectionId, updatedMappedPositions });
	};

	const handleAddPayrollPosition = (systemPosition: string) => {
		const updatedMappedPositions = props.mappedPositions.map((mappedPosition) => {
			return mappedPosition.systemPosition === systemPosition
				? {
						...mappedPosition,
						payrollPosition: [...(mappedPosition?.payrollPosition || []), ""],
				  }
				: mappedPosition;
		});

		const sectionId = props.id;
		props.onSetAllPositionsMapping({ sectionId, updatedMappedPositions });
	};

	const handleChangePayrollPosition = (value: string, systemPosition: string, order: number) => {
		const prevPosValue = props.mappedPositions.find((item) => item.systemPosition === systemPosition)?.payrollPosition[order];
		const updatedMappedPositions = props.mappedPositions.map((item) => {
			if (item.systemPosition === systemPosition) {
				let payrollPosition = item.payrollPosition;
				if (payrollPosition.length === 1 && !value) {
					payrollPosition = [];
				} else if (!payrollPosition.length) {
					payrollPosition = [value];
				} else {
					payrollPosition = payrollPosition.map((elem: string, index: number) => (index === order ? value : elem));
				}

				return {
					...item,
					payrollPosition,
				};
			}
			return item;
		});

		let updatedAvailablePositions = props.selectablePositions.filter((item) => item.label !== value);
		if (prevPosValue) {
			updatedAvailablePositions = [...updatedAvailablePositions, { key: prevPosValue, label: prevPosValue }];
		}

		const sectionId = props.id;
		props.onSetAllPositionsMapping({
			sectionId,
			updatedMappedPositions,
			updatedAvailablePositions,
		});
	};

	const handleDeletePayrollPosition = (order: number, systemPosition: string) => {
		const prevPosValue = props.mappedPositions.find((item) => item.systemPosition === systemPosition)?.payrollPosition?.[order] || "";
		const foundedMappedPosition = props.mappedPositions.find((item) => item.systemPosition === systemPosition);
		if (!foundedMappedPosition) {
			return;
		} else {
			foundedMappedPosition.payrollPosition.splice(order, 1);
		}

		const updatedMappedPositions = props.mappedPositions.map((item) => {
			return item.systemPosition === systemPosition ? foundedMappedPosition : item;
		});

		const updatedAvailablePositions: LabelObject[] = [...props.selectablePositions, { key: prevPosValue, label: prevPosValue }];
		const sectionId = props.id;
		props.onSetAllPositionsMapping({ sectionId, updatedMappedPositions, updatedAvailablePositions });
	};

	return (
		<>
			<Grid container direction="row">
				<Grid item sx={{ marginBottom: 1 }}>
					{props.isLoading ? (
						<Skeleton variant="rectangular" width={280} height={28} sx={{ borderRadius: "4px" }} />
					) : (
						<Typography className="page-title">{props.title}</Typography>
					)}
				</Grid>

				<Grid container direction="row" className="position-mapping-row">
					<Grid item xs={2.5} className="position-mapping-rtc-column">
						{props.isLoading ? (
							<Skeleton variant="rectangular" width={120} height={25} sx={{ borderRadius: "4px" }} />
						) : (
							<Typography className="position-mapping-table-header">Dealerwave Position</Typography>
						)}
					</Grid>
					<Grid item xs={9.5} className="position-mapping-payroll-column">
						{props.isLoading ? (
							<Skeleton variant="rectangular" width={150} height={25} sx={{ borderRadius: "4px" }} />
						) : (
							<Typography className="position-mapping-table-header">Payroll Position</Typography>
						)}
					</Grid>
				</Grid>
			</Grid>
			<Stack>
				{props.isLoading
					? Array.from({ length: 4 }).map(() => (
							<Grid item key={uuidv4()}>
								<Grid container direction="row" className="position-mapping-row" alignItems="flex-start">
									<Grid item className="position-mapping-rtc-column" sx={{ paddingTop: "5px" }}>
										<Skeleton variant="rectangular" width={150} height={15} sx={{ borderRadius: "4px" }} />
									</Grid>
									<Grid item className="position-mapping-payroll-column" sx={{ position: "relative" }}>
										<Stack direction={"row"} alignItems={"center"}>
											<Skeleton variant="rectangular" width={200} height={30} sx={{ borderRadius: "6px" }} />
											<Skeleton variant="circular" width={22} height={22} sx={{ ml: 1 }} />
										</Stack>
									</Grid>
								</Grid>
							</Grid>
					  ))
					: props.mappedPositions.map((item) => {
							return (
								<PositionMappingSectionRow
									// Data
									key={item.systemPosition}
									item={item}
									title={props.title}
									selectablePositions={props.selectablePositions}
									switchablePositions={switchablePositions}
									hasEditPermission={props.hasEditPermission}
									isMutating={props.isMutating}
									getEmptyCells={getEmptyCells}
									// Options
									allowDeleteFirst={props.allowDeleteFirst}
									showErrors={props.showErrors}
									isDropdownRequired={props.isDropdownRequired}
									isExpanded={notCollapsed.includes(item.systemPosition)}
									toggleRowExpansion={toggleRowExpansion}
									// Styles
									iconStyle={iconStyle}
									// Callbacks
									onChangePositionCategory={handleUpdateCapSwitch}
									onChangePayrollPosition={handleChangePayrollPosition}
									onDeletePayrollPosition={handleDeletePayrollPosition}
									onAddPayrollPosition={handleAddPayrollPosition}
								/>
							);
					  })}
			</Stack>
		</>
	);
}
